import React, { useState } from "react";
import { Image as ImageIcon, Save, RotateCcw, AlertCircle, CheckCircle2 } from "lucide-react";
import { useBranding, setBranding, resetBranding, BRANDING_DEFAULTS } from "@/lib/brandingStore";

const Field = ({ label, hint, name, value, onChange, placeholder, testId }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street">
      {label}
    </span>
    <input
      type="url"
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-1 w-full rounded-md border border-[#262626] bg-[#0A0A0A] py-2 px-3 text-sm text-white placeholder:text-white/30 focus:border-[#FF9500] focus:outline-none transition-colors"
    />
    {hint && <div className="mt-1 text-[11px] text-white/45 leading-snug">{hint}</div>}
  </label>
);

export const AdminBranding = () => {
  const current = useBranding();
  const [form, setForm] = useState(current);
  const [saved, setSaved] = useState(false);

  // Re-sync if external changes happen.
  React.useEffect(() => {
    setForm(current);
  }, [current]);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSaved(false);
  };

  const save = (e) => {
    e.preventDefault();
    setBranding(form);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  const reset = () => {
    if (!window.confirm("Restaurer le logo par défaut ?")) return;
    resetBranding();
  };

  return (
    <div data-testid="admin-page-branding" className="space-y-8">
      <header>
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#FF9500] font-street">
          Identité visuelle
        </div>
        <h1 className="font-street mt-2 text-3xl tracking-wide">Logo / Branding</h1>
        <p className="mt-2 text-sm text-white/55 leading-snug max-w-2xl">
          Modifiez l'URL du logo principal. Une fois enregistré, le logo s'applique
          immédiatement sur le site (header, hero, login admin). En Phase 1, vous
          pourrez aussi téléverser une image directement.
        </p>
      </header>

      <form onSubmit={save} className="grid gap-6 lg:grid-cols-2">
        {/* Aperçu */}
        <div className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 font-street mb-3">
            Aperçu du logo principal
          </div>
          <div className="flex items-center justify-center rounded-xl border border-[#262626] bg-[#0A0A0A] h-56 overflow-hidden">
            {form.logo_url ? (
              <img
                src={form.logo_url}
                alt="Aperçu logo"
                data-testid="admin-branding-preview"
                className="max-h-48 max-w-full object-contain"
                onError={(e) => {
                  e.target.style.opacity = "0.3";
                }}
              />
            ) : (
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <ImageIcon size={16} />
                Aucun logo
              </div>
            )}
          </div>
        </div>

        {/* Champs */}
        <div className="space-y-5">
          <Field
            label="URL du logo principal"
            hint="Affiché dans le header, le hero d'accueil et l'écran de connexion admin."
            name="logo_url"
            value={form.logo_url}
            onChange={onChange}
            placeholder="https://…/logo.png"
            testId="admin-branding-logo-url"
          />
          <Field
            label="URL du logo transparent (optionnel)"
            hint="Réservé aux futurs cas d'usage (fond clair, exports presse)."
            name="logo_transparent_url"
            value={form.logo_transparent_url}
            onChange={onChange}
            placeholder="https://…/logo-transparent.png"
            testId="admin-branding-logo-transparent-url"
          />
          <Field
            label="Image de partage (Open Graph)"
            hint="1200×630 recommandé. Utilisée par Facebook, WhatsApp, etc."
            name="og_image_url"
            value={form.og_image_url}
            onChange={onChange}
            placeholder="https://…/og-image.jpg"
            testId="admin-branding-og-url"
          />

          {saved && (
            <div
              role="status"
              data-testid="admin-branding-saved"
              className="inline-flex items-center gap-2 rounded-md border border-[#22C55E]/40 bg-[#22C55E]/10 px-3 py-2 text-[12px] text-[#86EFAC]"
            >
              <CheckCircle2 size={13} /> Modifications enregistrées.
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="submit"
              data-testid="admin-branding-save"
              className="font-street inline-flex items-center gap-2 rounded-md bg-[#FF9500] px-5 py-2.5 text-sm tracking-wider text-black shadow-[0_4px_0_#7a4900] hover:-translate-y-0.5 transition-all"
            >
              <Save size={14} />
              Enregistrer
            </button>
            <button
              type="button"
              onClick={reset}
              data-testid="admin-branding-reset"
              className="font-street inline-flex items-center gap-2 rounded-md border border-[#262626] px-4 py-2.5 text-sm tracking-wider text-white/70 hover:text-white hover:border-white/30 transition-colors"
            >
              <RotateCcw size={13} /> Réinitialiser
            </button>
          </div>

          <div className="text-[11px] text-white/40 leading-snug">
            <AlertCircle size={11} className="inline-block mr-1 -mt-0.5" />
            Données stockées localement (Phase 0). Elles ne se synchronisent pas
            encore entre appareils — branchement Mongo prévu en Phase 1.
          </div>
        </div>
      </form>

      <div className="text-xs text-white/40 italic">
        Logo par défaut : <code className="text-white/60">{BRANDING_DEFAULTS.logo_url.slice(0, 64)}…</code>
      </div>
    </div>
  );
};

export default AdminBranding;
